import React, { useEffect, useState } from "react";
import { Alert, Badge, Spinner, Table } from "react-bootstrap";
import apiClient from "@shared/services/apiClient";
import { getErrorMessage, unwrapApiData } from "./customizationUtils";

type Mapping = {
  mapping_id: string; source_system_uri: string; source_version: string; source_code: string; source_display?: string;
  target_system_uri: string; target_version: string; target_code: string; target_display?: string;
  relationship: string; status: string; version: number;
};

/** Display helper retained for imported legacy codes; provenance is never inferred from the prefix. */
export function formatVocabularyColumnCode(code: string, ontology: "ACNS" | "SCORE" | "RESEARCH") {
  const prefix = `${ontology}:`;
  return code.toUpperCase().startsWith(prefix) ? code.slice(prefix.length) : code;
}

export default function VocabularyMappingAdminPanel() {
  const [rows, setRows] = useState<Mapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => { void apiClient.get("/eeg/terminology-mappings")
    .then((response) => setRows(unwrapApiData<Mapping[]>(response) || []))
    .catch((reason) => setError(getErrorMessage(reason))).finally(() => setLoading(false)); }, []);
  return <section data-testid="vocabulary-mapping-admin" aria-readonly="true">
    <div className="d-flex gap-2 align-items-center"><h3 className="h6 mb-0">Terminology provenance</h3><Badge bg="secondary" data-testid="vocabulary-readonly-badge">Read-only governed mappings</Badge></div>
    <p className="text-muted small mt-2">Versioned mappings between local concepts and external terminology systems. Only reviewed mappings may be used for interoperable export.</p>
    {error && <Alert variant="danger">{error}</Alert>}
    {loading ? <Spinner size="sm" /> : rows.length === 0 ? <Alert variant="warning">No reviewed terminology mappings are configured. Local labels must not be represented as standardized external codes.</Alert> :
      <Table size="sm" responsive><thead><tr><th>Source</th><th>Target</th><th>Relationship</th><th>Governance</th></tr></thead><tbody>{rows.map((row) => <tr key={row.mapping_id}>
        <td><strong>{row.source_display || row.source_code}</strong><br/><small>{row.source_system_uri} · {row.source_version} · {row.source_code}</small></td>
        <td><strong>{row.target_display || row.target_code}</strong><br/><small>{row.target_system_uri} · {row.target_version} · {row.target_code}</small></td>
        <td>{row.relationship}</td><td><Badge bg={row.status === "reviewed" ? "success" : "warning"}>{row.status}</Badge> v{row.version}</td>
      </tr>)}</tbody></Table>}
  </section>;
}
